"use client";

import { ArrowUpRight, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheSignatureLookbook() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridRefVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const artifacts = [{
    id: "STYLE_001",
    title: "Precision Balayage",
    meta: "STYLE_001 / BALAYAGE / ARTISTRY / 2024",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_balayage_hero.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_balayage_hero.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_balayage_hero.png-wvc-sizes",
    alt: "Soft hand-painted balayage highlights in natural light",
    span: "lg:col-span-7 lg:row-span-2"
  }, {
    id: "STYLE_002",
    title: "Modern Bob Cut",
    meta: "STYLE_002 / PRECISION / CUT / 2024",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_bob_cut.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_bob_cut.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_bob_cut.png-wvc-sizes",
    alt: "Sharp, modern bob haircut with clean lines",
    span: "lg:col-span-5 lg:row-span-1"
  }, {
    id: "STYLE_003",
    title: "Styling Session",
    meta: "STYLE_003 / FINISH / TEXTURE / 2024",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_styling_session.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_styling_session.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_styling_session.png-wvc-sizes",
    alt: "Behind the chair styling session with professional tools",
    span: "lg:col-span-5 lg:row-span-1"
  }, {
    id: "STYLE_004",
    title: "Vibrant Color",
    meta: "STYLE_004 / VIVID / COLOR / 2024",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_vibrant_color.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_vibrant_color.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_vibrant_color.png-wvc-sizes",
    alt: "Deep, vibrant color transformation with glossy finish",
    span: "lg:col-span-4"
  }, {
    id: "STYLE_005",
    title: "Bridal Updo",
    meta: "STYLE_005 / UPDO / ELEGANCE / 2024",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_bridal_updo.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_bridal_updo.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_bridal_updo.png-wvc-sizes",
    alt: "Intricate bridal updo with soft texture",
    span: "lg:col-span-4"
  }, {
    id: "STYLE_006",
    title: "Deep Conditioning",
    meta: "STYLE_006 / TREATMENT / CARE / 2024",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_conditioning_treatment.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_conditioning_treatment.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_conditioning_treatment.png-wvc-sizes",
    alt: "Luxurious deep conditioning treatment in progress",
    span: "lg:col-span-4"
  }];
  return <section data-section-id="4625" id="the-signature-lookbook" className="bg-background text-foreground py-32 px-6 lg:px-12">
      <div className="max-w-[1440px] mx-auto">
        {}
        <div ref={headerRef} className={`transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <div className="flex items-center gap-3 mb-10">
            <span className="font-mono text-xs tracking-wider text-primary uppercase">// SIGNATURE LOOKBOOK — 2026 COLLECTION</span>
            <div className="h-px flex-1 bg-border" />
            
          </div>

          {}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
            <h2 className="font-default text-5xl md:text-6xl lg:text-[5rem] font-semibold leading-[0.95] tracking-tight max-w-3xl">
              Artistry in every strand.
            </h2>
            <div className="flex items-center gap-4">
              
              <Button asChild variant="ghost" className="border border-border hover:border-primary hover:bg-transparent hover:text-primary font-mono text-xs uppercase tracking-wider h-11 px-5 rounded-md">
                <Link to="/LookbookPage">
                  View full gallery
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-4 transition-all duration-1000 ease-out ${gridRefVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {artifacts.map((item, i) => <div data-index={i} key={item.id} className={`group relative overflow-hidden border border-border bg-card rounded-md transition-all duration-300 hover:border-primary ${item.span} ${i === 0 ? "aspect-[4/5] lg:aspect-auto lg:min-h-[640px]" : "aspect-[4/3] lg:min-h-[310px]"}`}>
              {}
              <div className="absolute top-2 left-2 z-20 pointer-events-none">
                <Plus className="size-3 text-muted-foreground/50 group-hover:text-primary transition-colors duration-300" strokeWidth={1.5} />
              </div>
              <div className="absolute top-2 right-2 z-20 pointer-events-none">
                <Plus className="size-3 text-muted-foreground/50 group-hover:text-primary transition-colors duration-300" strokeWidth={1.5} />
              </div>
              <div className="absolute bottom-2 left-2 z-20 pointer-events-none">
                <Plus className="size-3 text-muted-foreground/50 group-hover:text-primary transition-colors duration-300" strokeWidth={1.5} />
              </div>
              <div className="absolute bottom-2 right-2 z-20 pointer-events-none">
                <Plus className="size-3 text-muted-foreground/50 group-hover:text-primary transition-colors duration-300" strokeWidth={1.5} />
              </div>

              {}
              <div className="absolute inset-0 overflow-hidden">
                <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
              </div>

              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/0 to-background/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {}
              <div className="absolute bottom-0 left-0 right-0 p-5 z-10 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 ease-out">
                <div className="font-mono text-[10px] tracking-wider text-primary mb-2 uppercase">
                  {item.meta}
                </div>
                <div className="font-default text-lg font-semibold text-foreground">
                  {item.title}
                </div>
              </div>

              {}
              <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                <div className="size-9 flex items-center justify-center bg-primary text-primary-foreground rounded-md">
                  <ArrowUpRight className="size-4" strokeWidth={2} />
                </div>
              </div>

              {}
              <div className="absolute top-4 left-4 z-10 group-hover:opacity-0 transition-opacity duration-300">
                <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase bg-background/70 backdrop-blur-sm px-2 py-1 rounded-md border border-border/50">
                  {item.id}
                </span>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-12 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">6 OF 240</span>
          <div className="h-px flex-1 bg-border" />
        </div>
      </div>
    </section>;
}